/**
 * Transport contract for the page-aware Prospero assistant (in-app chat,
 * currently mounted on /guide). Plain types on purpose: the request is
 * validated server-side by a class-validator DTO and the client treats the
 * thread as trusted display data, so no second validation library is pulled
 * into the shared bundle.
 */

import type { SurveySchema } from '../dto/survey/survey-schema.types';

export type AssistantMessageRole = 'USER' | 'ASSISTANT';

/** A citation the backend resolved from a tool result of this same turn. */
export type AssistantCitation =
  | { kind: 'GUIDE'; title: string; sectionTitle: string; url: string }
  | { kind: 'OFFICIAL'; title: string; url: string }
  | { kind: 'TENANT_DATA'; title: string; generatedAt: string };

/** Categories the assistant may pick for an archived document (safe subset of DocumentCategory). */
export const ASSISTANT_DOCUMENT_CATEGORIES = [
  'HACCP_TRAINING',
  'CALIBRATION',
  'HACCP',
  'WATER_ANALYSIS',
  'OTHER',
] as const;
export type AssistantDocumentCategory = (typeof ASSISTANT_DOCUMENT_CATEGORIES)[number];

export const ASSISTANT_EXPIRY_WARNING_DAYS = [7, 14, 30] as const;
export type AssistantExpiryWarningDays = (typeof ASSISTANT_EXPIRY_WARNING_DAYS)[number];

/** File staged for the archive-draft flow; storageKey is tenant-prefixed. */
export interface AssistantAttachmentDraft {
  storageKey: string;
  filename: string;
  mimeType: string;
}

/** Pending archive-document draft carried by an ARCHIVE_DOCUMENT_DRAFT action. */
export interface AssistantArchiveDraft {
  title: string;
  /** Plain-text body for an AI-generated note PDF; absent for uploaded files. */
  content?: string;
  /** ISO date (Europe/Rome). */
  expiresAt: string;
  expiryWarningDays: AssistantExpiryWarningDays;
  category: AssistantDocumentCategory;
  /** Fixed archive type used for reminder notes; created on confirmation when missing. */
  documentTypeName?: 'Promemoria';
  attachment?: AssistantAttachmentDraft;
}

/** How a temperature reached the assistant instead of the equipment card. */
export const ASSISTANT_DECLARED_READING_REASONS = [
  'display-spento',
  'attrezzatura-guasta',
  'non-misurabile',
  'lettura-dichiarata',
] as const;
export type AssistantDeclaredReadingReason = (typeof ASSISTANT_DECLARED_READING_REASONS)[number];

/**
 * A temperature reading the assistant PROPOSES, carried by a
 * REGISTER_TEMPERATURE_READING action. Nothing reaches the HACCP register
 * until the user confirms this card: a reading is a declaration of fact, and
 * the operator — not the model — has to make it.
 */
export interface AssistantTemperatureReadingDraft {
  equipmentId: string;
  equipmentName: string;
  temperature: number;
  reason: AssistantDeclaredReadingReason;
  /** Limits shown in the card; the server recomputes the verdict on confirm. */
  minTemperature: number | null;
  maxTemperature: number | null;
  /** Preview verdict against those limits — never what gets stored. */
  previewCompliant: boolean;
}

/**
 * Screenshot attached to a guide-hit action, rendered as a thumbnail in chat.
 * A type alias (not an interface) on purpose: the assistant repository casts
 * stored JSON rows back to AssistantAction, and object-literal type aliases
 * keep the implicit index signature that cast relies on.
 */
export type AssistantGuideImage = { path: string; alt: string };

/**
 * Structured reply options. The user's pick comes back as a text marker
 * `[scelta: <optionId> <label>]` so the whole flow stays on the message bus.
 *
 * Two producers, two cardinalities: the MODEL's own free-choice output
 * (`assistantReplySchema` in the backend prompt) stays at ALWAYS exactly 3 —
 * unchanged, it is a generic "product decision" shape used across the whole
 * chat, not specific to the NC interview. The SERVER-authored interview
 * questions (the CCP profile's own option sets, `serverOptionsFor` in the
 * backend reducer) can carry any number ≥ 2 — a "profilo unione" question
 * such as `causa` has up to 11 — and `display` tells the UI how to render
 * that count.
 */
export interface AssistantReplyOptions {
  mode: 'single' | 'multi';
  /** One question per turn, in plain language. */
  question: string;
  /** At least 2; `id` follows `<setKey>:<n>` (e.g. `azione-fatta:1`). */
  choices: { id: string; label: string }[];
  /**
   * How the UI renders this block, decided by the SERVER from `choices.length`
   * (`deriveAssistantOptionsDisplay` below) — never by the client: `chips`
   * (today's behaviour, ≤3) or `dropdown` (>3, a MUI Select still emitting the
   * same `[scelta: id label]` marker on selection).
   */
  display: 'chips' | 'dropdown';
  /**
   * Whether free text is a VALID way to answer this question (round 7, G4).
   * `false` only for a server-authored `choiceOnly` NC interview field
   * (`serverOptionsFor` in the backend reducer): free text is never
   * interpreted there (round 6), so the UI must not invite "oppure scrivi a
   * parole" for a question a typed answer can never satisfy — the composer
   * itself stays enabled, only the misleading hint is withheld. `true` for
   * the model's own generic reply options and for every other
   * server-authored question, unchanged behaviour.
   *
   * Round 8 fix (B3): the ONLY re-hydration path for a persisted options row
   * (`normalizeAssistantReplyOptions`, backend) reads this value BACK from
   * the stored JSON rather than hardcoding `true` — a `choiceOnly` field's
   * `false` must survive a chat-history reload. A row persisted before this
   * flag existed has no key at all; that normalization path (not this type)
   * is where the `true` default for that case lives.
   */
  freeTextAllowed: boolean;
  /**
   * A deterministic "Aggiungi foto" affordance for THIS question, when the
   * profile field declares `acceptsPhoto` — the UI renders a button (camera
   * icon, opens the device camera/gallery directly) instead of the model
   * inviting the operator to use the generic composer paperclip in prose.
   * The photo is uploaded through the SAME staging endpoint the composer's
   * paperclip already uses and attaches to the interview without requiring
   * the operator to type or send anything. `undefined` = no photo affordance
   * for this question (every pre-existing field, and every non-NC options
   * block), unchanged behaviour.
   */
  photoAction?: { fieldKey: string; label: string };
}

/** Above this many choices the UI renders a dropdown instead of chips. */
export const ASSISTANT_OPTIONS_DROPDOWN_THRESHOLD = 3;

/**
 * ONE derivation of `AssistantReplyOptions.display`, shared by every producer
 * (the backend's `serverOptionsFor` and `normalizeAssistantReplyOptions`):
 * never persisted as an independent flag, always recomputed from the choice
 * count so it can never drift from it.
 */
export function deriveAssistantOptionsDisplay(choiceCount: number): 'chips' | 'dropdown' {
  return choiceCount > ASSISTANT_OPTIONS_DROPDOWN_THRESHOLD ? 'dropdown' : 'chips';
}

/**
 * Source types of self-check records that can carry a non-conformity
 * document. Mirrors Prisma `NonConformitySourceType` (`attachExisting`),
 * a subset of `DocumentSourceType` — `EQUIPMENT` is excluded on purpose.
 */
export type AssistantNcSourceType =
  | 'TEMPERATURE'
  | 'CLEANING'
  | 'OIL'
  | 'PEST_CONTROL'
  | 'METAL_DETECTION'
  | 'BLAST_CHILLING'
  | 'COOKING_TEMPERATURE';

/**
 * Server-authored §7 verdict on a later (post-intervention) re-measurement,
 * re-derived from the record's OWN thresholds — never the model's prose.
 * `RESOLVED`/`STILL_OPEN` require a real numeric value judged against a real
 * threshold; anything else (no rule for this CCP, no value yet, no usable
 * threshold) is `UNKNOWN`, which states that plainly instead of guessing.
 */
export type AssistantNcClosureOutcome = 'RESOLVED' | 'STILL_OPEN' | 'UNKNOWN';

export interface AssistantNcClosureAssessment {
  outcome: AssistantNcClosureOutcome;
  /** Plain-Italian sentence for §7, e.g. "Valore rimisurato -19 °C, entro i limiti -30/-18 °C: la non conformità risulta rientrata." */
  statement: string;
}

/**
 * Payload collected in chat for the "Gestione non conformità" PDF. Generic
 * fields shared by every CCP plus `profileEvidence`, whose keys are the
 * stable evidence keys declared by the vertical profile (e.g. the key
 * `postInterventionTemp` for temperature records).
 */
export interface AssistantNcDocumentDraft {
  sourceType: AssistantNcSourceType;
  recordId: string;
  /** ISO date of the non-compliant reading. */
  recordDate: string;
  title: string;
  /** What emerged, in the operator's words cleaned up. */
  findings: string;
  /**
   * Round 12, item 3 (ALTA, PDF legal-truth): server-authored §1 summary —
   * `describeNc(record)` plus one deterministic sentence per ledger field the
   * profile marks `summarizedInSection1` (duration, goods state, actions,
   * fate, goods already dispatched, cause, prevention — whichever the
   * operator actually picked). Present ONLY on an interview-driven draft; the
   * legacy model-driven draft tool never sets it, so the renderer falls back
   * to `findings` there, unchanged from before this field existed.
   */
  ledgerSummary?: string;
  /**
   * Round 12, item 3: the model's OWN confirmed narrative — printed in its
   * own labelled block ("Note dell'operatore raccolte dall'assistente"),
   * never merged into `findings`/`ledgerSummary`. Absent when the operator
   * never wrote one, or when a later ledger change invalidated it (see
   * `choiceLedgerFingerprint`, backend) — dropped rather than guessed.
   */
  operatorNarrative?: string;
  immediateAction: string;
  productFate?: string;
  rootCause?: string;
  preventiveAction?: string;
  /**
   * Profile-specific evidence with STABLE keys from the vertical profile.
   * `label` is the plain-language (Italian) label of the profile field,
   * resolved by the backend so the UI never humanizes keys.
   */
  profileEvidence?: { key: string; label: string; value: string }[];
  /**
   * Photo evidence staged in chat (`[allegato: ...]` marker → POST
   * /assistant/attachments), embedded in the PDF "Prove fotografiche" page.
   * Max 3; `attachmentId` doubles as the tenant-scoped storage key.
   */
  photoAttachments?: {
    attachmentId: string;
    filename: string;
    storageKey: string;
    /**
     * Human caption naming the interview field this photo was attached for
     * (e.g. "Foto della merce"), set only by the deterministic "Aggiungi
     * foto" chat button (Fase 3 side-fix). Optional: the questionnaire
     * upload path and any other attachment leave it undefined.
     */
    fieldCaption?: string;
  }[];
  /**
   * OPTIONAL (additive): the §7 closure verdict, present only when the
   * caller resolved it (see `assessNcClosure` in the backend). Absent means
   * "not computed for this draft", never "assessed and unknown" — that case
   * is `outcome: 'UNKNOWN'` with its own statement.
   */
  closureAssessment?: AssistantNcClosureAssessment;
  /**
   * Decision 15c ("Azioni ancora aperte"): labels of every picked choice the
   * vertical profile declares `openAction: true` — a withdrawal to start, an
   * authority still to inform, a verification still pending with the
   * manager. Resolved server-side (`resolveOpenActionLabels`, backend
   * `non-conformity/domain/nc-open-actions.ts`) from the SAME ledger both the
   * chat and the questionnaire build, so the renderer never hardcodes which
   * choices count as "still open" and both channels agree on the answer.
   * Absent/empty = no open action, the outcome may read as resolved.
   */
  openActionLabels?: string[];
  /**
   * Fase 3 part A ("ricontrollo", decision 3): `dueDate` (ISO `YYYY-MM-DD`,
   * already canonicalised — today admitted, shifted past a tenant closure)
   * when the operator scheduled a recheck of the corrective action, or
   * `waivedReason` when they explicitly chose not to. Absent only for a
   * legacy draft signed before this feature existed. Drives the PDF's
   * recheck box (`nc-pdf.service.ts`) and the `NcFollowUp` row
   * `NcDocumentFinalizer` creates right after the corrective-action link.
   */
  recheck?: { dueDate: string } | { waivedReason: string };
}

/**
 * The consultant deadline tracker: the only page where the deadline tools
 * exist (backend capability) and the deadline assistant shell mounts
 * (frontend). One definition so the two sides never drift.
 */
export const CONSULTANT_DEADLINES_ASSISTANT_PAGE = '/consultant/deadlines';

export function isConsultantDeadlinesAssistantPage(pageUrl: string): boolean {
  return (
    pageUrl === CONSULTANT_DEADLINES_ASSISTANT_PAGE ||
    pageUrl === `${CONSULTANT_DEADLINES_ASSISTANT_PAGE}/`
  );
}

/** Same cap as the dialog's multi-client creation (`CONSULTANT_BULK_MAX_TENANTS`). */
export const ASSISTANT_DEADLINE_DRAFT_MAX_CLIENTS = 50;
/** Calendar links a reply that cites deadlines may carry. */
export const ASSISTANT_DEADLINE_LINKS_MAX = 5;

/** Draft fields that can come from the AI reading of an attached document. */
export const ASSISTANT_DEADLINE_AI_FIELDS = [
  'documentType',
  'issuedAt',
  'dueAt',
  'validityMonths',
] as const;
export type AssistantDeadlineAiField = (typeof ASSISTANT_DEADLINE_AI_FIELDS)[number];

export type AssistantDeadlineDomain = 'FOOD_SAFETY' | 'WORKPLACE_SAFETY' | 'LEGAL';

/** Type aliases, not interfaces: stored JSON rows are cast back to AssistantAction. */
export type AssistantDeadlineDraftDocument = {
  /** One-shot staging token of the client (24 h), consumed by the confirm. */
  uploadToken: string;
  filename: string;
  /** Fields the card labels "Proposto dall'IA: verifica". */
  aiFields: AssistantDeadlineAiField[];
};

/**
 * Pending deadline creation carried by a CREATE_CONSULTANT_DEADLINES action.
 * Same meaning as `ConsultantCreateDeadlineDto`; every id was returned by a
 * tool of the turn that produced it, and the confirm re-checks everything.
 */
export type AssistantConsultantDeadlinesDraft = {
  clients: { tenantId: string; name: string }[];
  documentTypeId: string | null;
  documentTypeName: string | null;
  /** Free document name: only without a type or with the catalog "Altro". */
  title: string | null;
  domain: AssistantDeadlineDomain | null;
  /** Always resolved (YYYY-MM-DD): from dueAt, or issuedAt + validityMonths. */
  dueAt: string;
  issuedAt: string | null;
  validityMonths: number | null;
  warningDays: number[];
  /** Warnings the user asked for that would fall before today: the card says so. */
  droppedWarningDays: number[];
  /** Who uploads the document and its renewals: always true (the client) without a document. */
  clientCanUpload: boolean;
  document: AssistantDeadlineDraftDocument | null;
};

/** No `duplicate`: several deadlines of one type for the same client are allowed. */
export const ASSISTANT_DEADLINE_SKIP_REASONS = [
  'subscription_blocked',
  'out_of_perimeter',
  'failed',
  /** History reload: the deadline does not exist and the original reason was not stored. */
  'not_created',
] as const;
export type AssistantDeadlineSkipReason = (typeof ASSISTANT_DEADLINE_SKIP_REASONS)[number];

export type AssistantConsultantDeadlineOutcome =
  | {
      tenantId: string;
      tenantName: string;
      status: 'created';
      deadlineId: string;
      dueAt: string | null;
    }
  | {
      tenantId: string;
      tenantName: string;
      status: 'skipped';
      reason: AssistantDeadlineSkipReason;
    };

export type AssistantDeadlineLink = {
  deadlineId: string;
  label: string;
  tenantName: string;
  /** YYYY-MM-DD: only dated deadlines can be opened in the calendar. */
  dueAt: string;
};

/** Link of an OPEN_MY_DEADLINES card: the tenant's own deadlines, so no client name and `dueAt` may be missing. */
export type AssistantMyDeadlineLink = {
  deadlineId: string;
  label: string;
  /** YYYY-MM-DD; null for a deadline without a date. */
  dueAt: string | null;
};

export type AssistantAction =
  | {
      id: string;
      kind: 'OPEN_GUIDE_HIT';
      label: string;
      /** Relative pathname only (e.g. `/guide/ingredients`). */
      url: string;
      /** Section heading used to scroll/highlight the destination topic. */
      highlight: string;
      /** Screenshots of the cited topic, top of the document first (max 2). */
      images?: AssistantGuideImage[];
    }
  | {
      id: string;
      kind: 'OPEN_MANUAL_SECTION';
      label: string;
      /** Relative pathname of the draft page (e.g. `/doc-generation/:templateId`). */
      url: string;
      /** Stable key of the manual section to scroll to. */
      sectionKey: string;
      /**
       * Verbatim visible text to highlight inside the section; absent = the
       * whole section is flashed. Server-validated against the section text.
       */
      quote?: string;
    }
  | {
      id: string;
      kind: 'ARCHIVE_DOCUMENT_DRAFT';
      label: string;
      draft: AssistantArchiveDraft;
      /** Present when confirmation already created the archived document. */
      documentId?: string;
    }
  | {
      id: string;
      kind: 'OPEN_ARCHIVE_SEARCH';
      label: string;
      /** Relative `/documents` URL whose query params reproduce the search. */
      url: string;
      /** Total documents matching the filters (the reply shows at most 3). */
      total: number;
    }
  | {
      id: string;
      kind: 'OPEN_SUPPLIER';
      label: string;
      /** Relative `/suppliers/:id/edit` URL of the best-matching supplier. */
      url: string;
      /** Total suppliers matching the search (the reply shows at most 3). */
      total: number;
    }
  | {
      id: string;
      kind: 'OPEN_CUSTOMER';
      label: string;
      /** Relative `/customers/:id/edit` URL of the best-matching customer. */
      url: string;
      /** Total customers matching the search (the reply shows at most 3). */
      total: number;
    }
  | {
      id: string;
      kind: 'GENERATE_NC_DOCUMENT';
      label: string;
      draft: AssistantNcDocumentDraft;
      /** Present when confirmation already created the signed NC document. */
      documentId?: string;
    }
  | {
      id: string;
      kind: 'REGISTER_TEMPERATURE_READING';
      label: string;
      reading: AssistantTemperatureReadingDraft;
      /** Present when confirmation already wrote the reading to the register. */
      recordId?: string;
      /** Verdict the HACCP validator computed on confirm; absent until then. */
      isCompliant?: boolean;
    }
  | {
      id: string;
      kind: 'DOWNLOAD_FILE';
      label: string;
      /** Presigned GET url on external storage — open in a NEW tab, never navigate. */
      url: string;
      fileName: string;
    }
  | {
      id: string;
      kind: 'CREATE_CONSULTANT_DEADLINES';
      label: string;
      draft: AssistantConsultantDeadlinesDraft;
      /**
       * Present once confirmed: saved on the action by the confirm (skip reasons included);
       * cards confirmed before that show the rows that exist (`not_created` for the others).
       */
      outcomes?: AssistantConsultantDeadlineOutcome[];
    }
  | {
      id: string;
      kind: 'OPEN_CONSULTANT_DEADLINES';
      label: string;
      links: AssistantDeadlineLink[];
      /** Relative `/consultant/deadlines?...` URL reproducing the filters; `links` may be empty when set. */
      url?: string;
      /** Rows the search matched; the card shows at most ASSISTANT_DEADLINE_LINKS_MAX. */
      total: number;
    }
  | {
      id: string;
      kind: 'OPEN_MY_DEADLINES';
      label: string;
      links: AssistantMyDeadlineLink[];
      /** Relative `/documents?...` URL reproducing the filters. */
      url?: string;
      /** Rows the search matched; the card shows at most ASSISTANT_DEADLINE_LINKS_MAX. */
      total: number;
    };

export type CreateConsultantDeadlinesAction = Extract<
  AssistantAction,
  { kind: 'CREATE_CONSULTANT_DEADLINES' }
>;
export type OpenConsultantDeadlinesAction = Extract<
  AssistantAction,
  { kind: 'OPEN_CONSULTANT_DEADLINES' }
>;

export interface AssistantConsultantDeadlinesConfirmResponse {
  outcomes: AssistantConsultantDeadlineOutcome[];
}

export interface AssistantMessage {
  id: string;
  role: AssistantMessageRole;
  text: string;
  citations: AssistantCitation[];
  actions: AssistantAction[];
  /** Structured choices the assistant proposed with this reply (absent on most turns). */
  options?: AssistantReplyOptions | null;
  /**
   * On interview turns (self-check NC flow), the profile field the server
   * options block belongs to. Response-only: not persisted on history rows.
   */
  interviewFieldKey?: string;
  /** Relative pathname the turn was sent from; empty for opening greetings. */
  pageUrl: string;
  createdAt: string;
}

export interface AssistantThread {
  messages: AssistantMessage[];
}

export interface AssistantSendRequest {
  /** `null` opens the conversation with an assistant greeting. */
  text: string | null;
  /** Current `location.pathname`, never a full URL. */
  pageUrl: string;
  /**
   * Unsaved section edits from the manual draft editor (manual-page turns
   * only): overlaid onto the compiled manual state for this turn.
   */
  manualDraftOverlay?: AssistantManualDraftOverlayItem[];
  /**
   * Survey-authoring turn (studio builder page only): the template being
   * edited plus the client-side schema overlay. `null`/absent schema means
   * "use the persisted DRAFT as the source of truth for this turn"; when the
   * builder has unsaved local edits the client passes the overlay (same
   * pattern as `manualDraftOverlay`). The turn itself is tenant-resolved to
   * the consultant's CONSULTANT workspace server-side.
   */
  surveyAuthoring?: AssistantSurveyAuthoringContext | null;
}

/**
 * Per-turn context of the survey-authoring assistant (request side).
 * `templateId` is validated against the caller's OWNED consultant templates
 * server-side; the schema, when present, is the turn's source of truth.
 */
export interface AssistantSurveyAuthoringContext {
  templateId: string;
  schema: SurveySchema | null;
}

/**
 * Result of the survey-authoring turn (response side). The schema returned is
 * the last schema PERSISTED by the `applySurveySchemaEdit` tool this turn —
 * never the model's free text. `validationErrors` are the codes of
 * `validateSchema` still open; `untranslatedEnCount` counts I18nText fields
 * whose `en === it` (normative quotes excluded) so the UI can push a
 * translation pass before the consultant considers the turn done.
 */
export interface AssistantSendResponse {
  messages: AssistantMessage[];
  surveyAuthoring?: {
    schema: SurveySchema;
    validationErrors: string[];
    untranslatedEnCount: number;
  } | null;
}

export const ASSISTANT_TEXT_MAX_LENGTH = 2000;
export const ASSISTANT_PAGE_URL_MAX_LENGTH = 500;
export const ASSISTANT_THREAD_VISIBLE_MESSAGES = 40;
export const ASSISTANT_PROMPT_HISTORY_MESSAGES = 20;
export const ASSISTANT_ACTIONS_PER_REPLY = 1;
/** Hard cap for `AssistantSurveyAuthoringContext.templateId` (UUIDs are 36 chars). */
export const ASSISTANT_SURVEY_AUTHORING_TEMPLATE_ID_MAX_LENGTH = 64;
/**
 * Hard cap for the JSON-serialized size of
 * `AssistantSurveyAuthoringContext.schema` (the turn's overlay): the schema
 * rides verbatim into the authoring system prompt, so the request payload
 * bounds what the prompt can carry — the same role
 * `ASSISTANT_MANUAL_OVERLAY_SECTION_MAX_CHARS` plays for the manual copilot.
 * Enforced server-side BEFORE the turn lock / quota / model call with a
 * structured 400 (`assistant.authoring_schema_too_large`).
 */
export const ASSISTANT_SURVEY_AUTHORING_SCHEMA_MAX_CHARS = 200000;

/**
 * Unsaved section edits riding a manual-page message so the copilot's compiled
 * state matches what the consultant sees on screen, not just the saved draft.
 * Hard caps on both sides (FE collector and backend DTO): the overlay replaces
 * content only for sections the draft actually has, after sanitization.
 */
export const ASSISTANT_MANUAL_OVERLAY_MAX_SECTIONS = 3;
export const ASSISTANT_MANUAL_OVERLAY_SECTION_MAX_CHARS = 30000;

export interface AssistantManualDraftOverlayItem {
  /** Stable key of a section of the open draft. */
  sectionKey: string;
  /** Current editor HTML (sanitized server-side before use). */
  contentHtml: string;
}
